// The player effects of the map (R910, `ui-map-interface.md` §6.6).
//
// An effect is a slow visual animation a player wears on a fleet, a round or
// a hold. The server resolves the pick, the class-reveal gate and the
// autopilot gate, and stamps one catalog wire id on the object; this layer
// draws what the number names and decides nothing.
//
// **One class, two instances, two slots** (R914). A hold's effect always draws
// UNDER the wakes, the hulls and the rounds — the owner's ruling — so it
// takes the `holdeffects` slot right after `sites`. A fleet's and a round's
// effect draws OVER its own silhouette and under `fx`, so it takes the
// `effects` slot right after `torpedoes`. Two slots, one body.
//
// The arithmetic is `render/effect-styles.ts`, which is a leaf with no
// engine in it. This layer owns the clock, the ink, the phase, the cull and
// the three sinks, and it hands the leaf ONE reused context (R131).
//
// **Reduced motion freezes the clock and keeps the mark** (R915). A viewer who
// asked the OS for less motion still sees who wears what; the animation
// stops. Every recipe is a pure function of `ctx.t`, so holding the clock is
// the whole of the rule (R184).

import { factionColor } from '../../world';
import { reducedMotion } from '../../render/motion';
import { CORE_INK } from '../../render/recipe-kit';
import {
  EFFECT_BY_WIRE, ownerPhase, type EffectCtx, type EffectKind,
} from '../../render/effect-styles';
import { F_ADD, outside, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, QuadSink, SegmentSink } from './batches';
import { zoomOf } from './shared';
import type { RoundSnap } from './torpedoes';
import type { HoldSnap } from './sites';

/**
 * One hull, as this layer reads it.
 *
 * `ChartFleet` satisfies it, so the fleet layer grows no third buffer: the
 * chart's snapshot already carries the place, the heading, the fade and the
 * owner, and `effect` joined it with the wire (R910).
 */
export interface EffectFleet {
  readonly id: number;
  readonly faction: number;
  readonly x: number;
  readonly y: number;
  readonly heading: number;
  readonly alpha: number;
  readonly ownerPlayer: number;
  readonly effect: number;
}

/** Where one instance of the layer gets its objects. */
export interface EffectFeeds {
  fleets?(): readonly EffectFleet[];
  rounds?(): readonly RoundSnap[];
  holds?(): readonly HoldSnap[];
}

/** How many instances each sink holds before the engine grows it. */
const QUAD_CAPACITY = 512;
const SEGMENT_CAPACITY = 256;
const CIRCLE_CAPACITY = 128;

/**
 * An object with no account behind it takes its faction's phase instead.
 *
 * `FleetState.owner_player` is own-faction only and a round carries no owner
 * at all, so an enemy contact has no account to hash. The faction is what is
 * left, and it is hashed as a NEGATIVE number so it can never collide with
 * an account id.
 */
function phaseOf(ownerPlayer: number, faction: number): number {
  return ownerPhase(ownerPlayer !== 0 ? ownerPlayer : -faction);
}

export class EffectLayer implements Layer {
  private shown = true;

  private readonly quads: QuadSink;
  private readonly segs: SegmentSink;
  private readonly rings: CircleSink;
  /** The one context every recipe of every frame is handed (R131). */
  private readonly ctx: EffectCtx;

  /** The layer's own clock, in seconds. It is HELD under reduced motion. */
  private t = 0;
  /**
   * Whether the clock is held, read ONE time.
   *
   * The answer is a `matchMedia` call, which is a DOM read, and a viewer
   * does not change the OS switch mid-match. Reading it in the constructor
   * therefore keeps the per-frame path clean (§10.4).
   */
  private readonly held: boolean;

  constructor(
    readonly name: string,
    src: BatchSource,
    private readonly feeds: EffectFeeds,
  ) {
    this.quads = src.newQuads(QUAD_CAPACITY);
    this.segs = src.newSegments(SEGMENT_CAPACITY);
    this.rings = src.newCircles(CIRCLE_CAPACITY);
    this.held = reducedMotion();
    this.ctx = {
      kind: 'fleet', x: 0, y: 0, heading: 0, id: 0,
      t: 0, phase: 0, color: 0, alpha: 1, z: 1, add: F_ADD, speed: 0, u: 0,
      quads: this.quads, segs: this.segs, rings: this.rings,
      sprite: (key) => src.sprite(key),
    };
  }

  get visible(): boolean { return this.shown; }
  setVisible(v: boolean): void { this.shown = v; }

  /**
   * The eye-test override of the `fxfleet`, `fxround` and `fxhold` knobs:
   * a wire id every object of that kind wears instead of its own, 0 for
   * none. It changes nothing on the wire and it is never a viewer setting.
   */
  setPreview(fleet: number, round: number, hold: number): void {
    this.preview.fleet = fleet;
    this.preview.torpedo = round;
    this.preview.hold = hold;
  }
  private readonly preview = { fleet: 0, torpedo: 0, hold: 0 };

  /** The arrival fade (R838). The hold instance follows the sites, the
   *  other follows the fleets. */
  setAlpha(a: number): void {
    this.quads.globalAlpha = a;
    this.segs.globalAlpha = a;
    this.rings.globalAlpha = a;
  }

  /**
   * Re-points one feed while the layer lives.
   *
   * A hello builds a NEW site layer, so the hold feed must be handed over
   * again; nothing else in the scene is replaced mid-match.
   */
  setFeeds(feeds: EffectFeeds): void {
    this.feeds.fleets = feeds.fleets;
    this.feeds.rounds = feeds.rounds;
    this.feeds.holds = feeds.holds;
  }

  update(frame: FrameInfo): void {
    if (!this.held) this.t += frame.dtMs / 1000;
    const c = this.ctx;
    c.t = this.t;
    c.z = zoomOf(frame.cam.zoom);
    this.quads.begin();
    this.segs.begin();
    this.rings.begin();
    const { x0, y0, x1, y1 } = frame.cull;

    const fleets = this.feeds.fleets?.();
    if (fleets) {
      for (const f of fleets) {
        if (outside(f.x, f.y, x0, y0, x1, y1)) continue;
        this.pack('fleet', f.effect, f.id, f.x, f.y, f.heading,
          f.faction, f.ownerPlayer, f.alpha);
      }
    }

    const rounds = this.feeds.rounds?.();
    if (rounds) {
      for (const r of rounds) {
        if (r.hidden || outside(r.x, r.y, x0, y0, x1, y1)) continue;
        // A round in flight carries no fade of its own. It is the one
        // object that RUNS, so it is the one that hands the recipe a speed
        // and a run progress (R924).
        this.pack('torpedo', r.effect, r.id, r.x, r.y, r.heading,
          r.faction, 0, 1, r.speed, r.u);
      }
    }

    const holds = this.feeds.holds?.();
    if (holds) {
      for (const h of holds) {
        if (h.hidden || outside(h.x, h.y, x0, y0, x1, y1)) continue;
        // A hold does not point anywhere, so its heading is 0.
        this.pack('hold', h.effect, h.id, h.x, h.y, 0,
          h.faction, h.ownerPlayer, 1);
      }
    }
  }

  draw(ctx: DrawCtx): void {
    // The rings and the rays first, the sprites over them: a grain that a
    // ring crossed must read as the top of the mark.
    this.rings.flush(ctx.cam);
    this.segs.flush(ctx.cam);
    this.quads.flush(ctx.cam);
  }

  /**
   * One object's mark, or nothing at all.
   *
   * `speed` and `u` are the two trailing figures a ROUND carries (R924): a
   * fleet and a hold leave them out, and the context then reads 0.
   */
  private pack(
    kind: EffectKind, effect: number, id: number, x: number, y: number,
    heading: number, faction: number, ownerPlayer: number, alpha: number,
    speed = 0, u = 0,
  ): void {
    const wire = this.preview[kind] || effect;
    if (wire === 0 || alpha <= 0) return;
    const recipe = EFFECT_BY_WIRE[wire];
    if (!recipe) return;
    const c = this.ctx;
    c.kind = kind;
    c.x = x; c.y = y;
    c.heading = heading;
    c.id = id;
    c.phase = phaseOf(ownerPlayer, faction);
    // The theme never reaches here (themes.md §10): an effect wears the
    // owner's faction colour or the ink its own catalog row states.
    c.color = recipe.row.tint === 'faction'
      ? factionColor(faction)
      : (recipe.row.ink ?? CORE_INK);
    c.alpha = alpha;
    c.speed = speed;
    c.u = u;
    recipe.draw(c);
  }
}
